import api, { isApiError } from '@/services/api';
import type { ApiErrorEnvelope } from '@/services/api';
import type {
    ChangeCustomerNameInput,
    Customer,
    CustomerAmendment,
    CustomerAmendmentsPage,
    UpdateCustomerInput,
} from '../types/customers.types';
import type { NewCustomerFormInputs } from '../schemas/customer.schemas';

// ---------------------------------------------------------------------------
// camelCase ↔ snake_case translation helpers
// ---------------------------------------------------------------------------

/**
 * Translates form inputs (camelCase) into a Customer shape (snake_case).
 * The returned object has no `id` — it represents a "pending" customer
 * that has not yet been persisted to the backend.
 *
 * Optional fields are `undefined` rather than `''` so the Redux slice stays
 * clean. The wire payload (inside `createCustomer`) maps `undefined → ''`
 * to satisfy the backend's `nullable|string` validation.
 */
export const formInputsToCustomer = (input: NewCustomerFormInputs): Customer => ({
    title: input.title,
    forename: input.forename,
    surname: input.surname,
    address_line_1: input.addressLine1,
    address_line_2: input.addressLine2 || undefined,
    city: input.city,
    postcode: input.postcode,
    email: input.email,
    mobile_phone: input.mobilePhone,
    business_name: input.businessName || undefined,
    vat_number: input.vatNumber || undefined,
});

// ---------------------------------------------------------------------------
// Duplicate email in-scope error types
// ---------------------------------------------------------------------------

export interface DuplicateInScopeExistingCustomer {
    id: string;
    forename: string | null;
    surname: string | null;
    email: string | null;
    postcode: string | null;
    dealer_ids: string[];
}

/** The published wire code, mirroring CustomerController::CODE_DUPLICATE_EMAIL_IN_SCOPE.
 *  Declared once so the string appears exactly once on this side of the wire. */
export const DUPLICATE_EMAIL_IN_SCOPE = 'duplicate_email_in_scope' as const;

export interface DuplicateInScopeErrorBody {
    success: false;
    error: ApiErrorEnvelope;
    message: string;
    existing_customers: DuplicateInScopeExistingCustomer[];
}

export class DuplicateInScopeError extends Error {
    public readonly existingCustomers: DuplicateInScopeExistingCustomer[];

    // D7: takes the ALREADY-NORMALISED message rather than a raw body, so the
    // class never parses `message` off a response and survives slice 3's
    // retirement of the top-level compat key untouched.
    constructor(message: string, existingCustomers: DuplicateInScopeExistingCustomer[]) {
        super(message);
        this.name = 'DuplicateInScopeError';
        this.existingCustomers = existingCustomers;
        // Maintain correct prototype chain in transpiled environments.
        Object.setPrototypeOf(this, DuplicateInScopeError.prototype);
    }
}

export const isDuplicateInScopeError = (err: unknown): err is DuplicateInScopeError => {
    return err instanceof DuplicateInScopeError;
};

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

interface CustomerSearchParams {
    search?: string;
    page?: number;
    per_page?: number;
}

export interface CustomerSearchResponse {
    success: boolean;
    data: Customer[];
    meta: [current_page: number, last_page: number, per_page: number, total: number];
}

export interface CreateCustomerResponse {
    success: boolean;
    data: Customer;
    message?: string;
}

const customersPath = '/customers';

export const fetchCustomers = async (params: CustomerSearchParams = {}): Promise<Customer[]> => {
    // Build query parameters, only including non-empty values
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `${customersPath}${queryString ? `/?${queryString}` : '/'}`;

    // Fetch customers from the API
    const response = await api.get<CustomerSearchResponse>(url);
    if (!response.data.success) {
        throw new Error('Failed to fetch customers');
    }
    return response.data.data;
};

export const createCustomer = async (customerData: NewCustomerFormInputs): Promise<Customer> => {
    // Build the in-memory shape via the shared helper, then remap optional
    // `undefined` values to `''` for wire compatibility (backend validator
    // accepts both `nullable|string` forms).
    const pending = formInputsToCustomer(customerData);
    const apiData = {
        title: pending.title,
        forename: pending.forename,
        surname: pending.surname,
        address_line_1: pending.address_line_1,
        address_line_2: pending.address_line_2 ?? '',
        city: pending.city,
        postcode: pending.postcode,
        email: pending.email,
        mobile_phone: pending.mobile_phone ?? '',
        business_name: pending.business_name ?? '',
        vat_number: pending.vat_number ?? '',
    };

    try {
        const response = await api.post<CreateCustomerResponse>(`${customersPath}/`, apiData);

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to create customer');
        }

        return response.data.data;
    } catch (err) {
        // D6. `error` is the elemis envelope on this 422 (customers-service
        // migrated it in #948, alongside this read). The machine code is read
        // off ApiError.code, which the shared interceptor already lifted via
        // readApiError() (api.ts:93,127) -- the one sanctioned read. Do NOT
        // reintroduce a direct `body.error` comparison: `error` is an object.
        if (isApiError(err) && err.status === 422 && err.code === DUPLICATE_EMAIL_IN_SCOPE) {
            const body = err.data as Partial<DuplicateInScopeErrorBody> | undefined;
            if (Array.isArray(body?.existing_customers)) {
                throw new DuplicateInScopeError(err.message, body.existing_customers);
            }
        }
        throw err;
    }
};

export const fetchCustomerById = async (id: string): Promise<Customer> => {
    const response = await api.get<{ success: boolean; data: Customer }>(`${customersPath}/${id}`);
    if (!response.data.success) {
        throw new Error('Failed to fetch customer');
    }
    return response.data.data;
};

// ---------------------------------------------------------------------------
// Holder detail changes (Epic 3.7 slice C1a)
// ---------------------------------------------------------------------------

/** snake_case at the boundary. A blank optional value is omitted, not sent as
 *  '': absent and empty mean different things to the amendment diff. */
const CUSTOMER_FIELD_MAP: Record<keyof UpdateCustomerInput, string> = {
    addressLine1: 'address_line_1',
    addressLine2: 'address_line_2',
    city: 'city',
    postcode: 'postcode',
    email: 'email',
    mobilePhone: 'mobile_phone',
    businessName: 'business_name',
    vatNumber: 'vat_number',
    agreementId: 'agreement_id',
    requesterType: 'requester_type',
};

export const buildUpdateCustomerPayload = (input: UpdateCustomerInput): Record<string, string> => {
    const payload: Record<string, string> = {};

    (Object.keys(CUSTOMER_FIELD_MAP) as Array<keyof UpdateCustomerInput>).forEach((key) => {
        const value = input[key];
        if (value !== undefined && value !== '') {
            payload[CUSTOMER_FIELD_MAP[key]] = value;
        }
    });

    return payload;
};

/** Sends ONLY the fields the caller changed, plus the plan context, so a
 *  concurrent edit to an untouched field is not clobbered. */
export const updateCustomer = async (id: string, input: UpdateCustomerInput): Promise<Customer> => {
    const response = await api.put<{ success: boolean; data: Customer }>(
        `${customersPath}/${id}`,
        buildUpdateCustomerPayload(input),
    );

    return response.data.data;
};

// ---------------------------------------------------------------------------
// Holder name change and the amendment log (Epic 3.7 slice C1b)
// ---------------------------------------------------------------------------

export const CUSTOMER_AMENDMENTS_PER_PAGE = 20;

type RawNode = Record<string, unknown>;

const asRecord = (value: unknown): RawNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as RawNode) : {};

const asNullableString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asValueMap = (value: unknown): Record<string, string | null> =>
    Object.fromEntries(Object.entries(asRecord(value)).map(([key, entry]) => [key, asNullableString(entry)]));

// Page numbers are 1-based for the shared Pagination; only `total` may be 0.
const asInt = (value: unknown, min: number, fallback: number): number => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= min ? parsed : fallback;
};

/** Tolerates a missing key, so a node from an older or newer server still renders. */
export const mapAmendmentNode = (raw: unknown): CustomerAmendment => {
    const node = asRecord(raw);
    const newValues = asValueMap(node.new_values);

    return {
        id: asNullableString(node.id) ?? '',
        amendmentType: asNullableString(node.amendment_type) ?? '',
        reason: asNullableString(node.reason),
        requesterType: asNullableString(node.requester_type),
        evidenceType: asNullableString(node.evidence_type),
        agreementId: asNullableString(node.agreement_id),
        changedFields: Array.isArray(node.changed_fields)
            ? node.changed_fields.filter((field): field is string => typeof field === 'string')
            : Object.keys(newValues),
        oldValues: asValueMap(node.old_values),
        newValues,
        recordedAt: asNullableString(node.recorded_at) ?? '',
        actorType: asNullableString(node.actor_type),
        actorId: asNullableString(node.actor_id),
        actorName: asNullableString(node.actor_name),
    };
};

/** Order is the server's (newest first) and is never re-sorted here. */
export const fetchCustomerAmendments = async (
    id: string,
    params: { page: number; perPage: number },
): Promise<CustomerAmendmentsPage> => {
    const response = await api.get<{ data?: unknown; meta?: unknown }>(`${customersPath}/${id}/amendments`, {
        params: { page: params.page, per_page: params.perPage },
    });
    const meta = asRecord(response.data.meta);

    return {
        amendments: Array.isArray(response.data.data) ? response.data.data.map(mapAmendmentNode) : [],
        meta: {
            currentPage: asInt(meta.current_page, 1, params.page),
            lastPage: asInt(meta.last_page, 1, 1),
            perPage: asInt(meta.per_page, 1, params.perPage),
            total: asInt(meta.total, 0, 0),
        },
    };
};

export const buildChangeCustomerNamePayload = (input: ChangeCustomerNameInput): Record<string, string> => ({
    title: input.title,
    forename: input.forename,
    surname: input.surname,
    reason: input.reason,
    requester_type: input.requesterType,
    evidence_type: input.evidenceType,
    ...(input.agreementId ? { agreement_id: input.agreementId } : {}),
});

export const changeCustomerName = async (
    id: string,
    input: ChangeCustomerNameInput,
): Promise<{ customer: Customer; amendment: CustomerAmendment }> => {
    const response = await api.patch<{ data: Customer; amendment: unknown }>(
        `${customersPath}/${id}/name`,
        buildChangeCustomerNamePayload(input),
    );

    return { customer: response.data.data, amendment: mapAmendmentNode(response.data.amendment) };
};
